<template>
  <div class="flex flex-col gap-2">
    <ListItem :has-error="Array.isArray(errors) || !!generalErrorMessage" @click="showModal = true">
      <ListItemColumn
        :label="
          t(extraType === VehicleExtraType.EXTRA_KM ? 'fromExtraKmPrice' : 'fromPurchasePrice')
        "
      >
        {{ printPriceRange() }}
      </ListItemColumn>
      <CVButton variant="link" @click.stop="showOptions = !showOptions">{{
        t(!showOptions ? 'showOptions' : 'hideOptions')
      }}</CVButton>
      <DeleteButton @click.stop="$emit('delete')" />
    </ListItem>

    <p v-if="generalErrorMessage" class="text-error">
      {{ generalErrorMessage }}
    </p>

    <template v-if="showOptions">
      <PriceRangeWithQuantityOptions
        v-if="hasQuantityOptions"
        :price-range-index="priceRangeIndex"
        :extra-type="extraType"
      />
      <div v-else class="space-y-4 rounded-lg border border-hazel-200 p-2 pb-4">
        <div v-if="value.options.length" class="flex flex-col gap-2">
          <OptionListItem
            v-for="(_, i) in value.options"
            :key="i"
            :price-range-index="priceRangeIndex"
            :option-index="i"
            :extra-type="extraType"
            :has-error="errors?.some((err) => err === i) || !!generalErrorMessage"
            @delete="deleteOption(i)"
          />
        </div>
        <div v-else class="text-orange-900/60 mt-2 text-center">{{ t('noOptions') }}</div>
        <AddItemButton :label="t('addOption')" @click="addOption" />
      </div>
    </template>

    <Modal v-model="showModal" content-class="!overflow-y-visible">
      <PriceRangeForm
        :name-prefix="'priceRanges.' + priceRangeIndex"
        :extra-type="extraType"
        @close="showModal = false"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useField, useFieldArray } from 'vee-validate';
import { ref, watch } from 'vue';
import { formatCurrency } from '@/utils/format-numbers.ts';
import OptionListItem from '@/views/yield-management/edit-extras/OptionListItem.vue';
import ListItem from '../ui/ListItem.vue';
import ListItemColumn from '@/views/yield-management/ui/ListItemColumn.vue';
import DeleteButton from '@/views/yield-management/ui/DeleteButton.vue';
import PriceRangeForm from '@/views/yield-management/edit-extras/PriceRangeForm.vue';
import type { EditExtraDto } from '@/views/yield-management/edit-extras/EditExtrasView.vue';
import type { ArrayType } from '@/utils/array-type.ts';
import AddItemButton from '@/views/yield-management/ui/AddItemButton.vue';
import { Alert } from '@/services/alert.service';
import {
  type EditAutoAssignExtraOptionDto,
  ValueType,
  VehicleExtraOptionPricingType,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import PriceRangeWithQuantityOptions from './PriceRangeWithQuantityOptions.vue';
import Modal from '@/components/Modal.vue';

const props = defineProps<{
  priceRangeIndex: number;
  extraType: EditExtraDto['type'];
  hasQuantityOptions?: boolean;
  errors?: number[];
  generalErrorMessage?: string;
}>();

const emit = defineEmits<{
  (e: 'delete'): void;
  (e: 'close-modal'): void;
}>();

const { t } = useI18n();

const showOptions = ref(false);
const showModal = ref(false);

const { value } = useField<ArrayType<EditExtraDto['priceRanges']>>(
  'priceRanges.' + props.priceRangeIndex,
);
const { push, remove } = useFieldArray(`priceRanges.${props.priceRangeIndex}.options`);

const printPriceRange = () => {
  const toPrice = value.value.toPrice ? formatCurrency(value.value.toPrice) : '∞';
  return `${formatCurrency(value.value.fromPrice)} - ${toPrice}`;
};

const addOption = () => {
  const newOption: EditAutoAssignExtraOptionDto = {
    ...getDefaultValue(),
    price: null,
    pricePerDay: [],
    pricingType: VehicleExtraOptionPricingType.FIXED_PER_DAY,
  };
  push(newOption);
};

const deleteOption = async (index: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteOption'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }
  remove(index);
};

const getDefaultValue = () => {
  switch (props.extraType) {
    case VehicleExtraType.ADDITIONAL_DRIVER:
    case VehicleExtraType.DEDUCTIBLE:
    case VehicleExtraType.YOUNG_DRIVER:
    case VehicleExtraType.EXTRA_KM:
      return { valueType: ValueType.NUMBER, value: '0' };
    default:
      return { valueType: ValueType.BOOLEAN, value: 'false' };
  }
};

watch(showModal, () => {
  if (!showModal.value) {
    emit('close-modal');
  }
});
</script>

<i18n lang="json">
{
  "en": {
    "fromPurchasePrice": "Car purchase net price range",
    "fromExtraKmPrice": "Extra km price range",
    "showOptions": "Show options",
    "hideOptions": "Hide options",
    "addOption": "Add option",
    "noOptions": "No options",
    "reallyDeleteOption": "Do you really want to delete this option?"
  },
  "de": {
    "fromPurchasePrice": "Fahrzeug EK (Netto) Range",
    "fromExtraKmPrice": "Extra KM Preis Range",
    "showOptions": "Optionen anzeigen",
    "hideOptions": "Optionen ausblenden",
    "addOption": "Option hinzufügen",
    "noOptions": "Keine Optionen",
    "reallyDeleteOption": "Möchtest du diese Option wirklich löschen?"
  },
  "es": {
    "fromPurchasePrice": "Tramo de precio neto de compra",
    "fromExtraKmPrice": "Tramo de precio por km extra",
    "showOptions": "Mostrar opciones",
    "hideOptions": "Ocultar opciones",
    "addOption": "Añadir opción",
    "noOptions": "Sin opciones",
    "reallyDeleteOption": "¿Seguro que quieres eliminar esta opción?"
  }
}
</i18n>
